<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col md="12">
          <h5 class="section-label mb-3">
            {{ $t('View KYC Wallet') }}
          </h5>
        </b-col>
        <b-col
          md="12"
          class="align-items-center justify-content-start mb-1 mb-md-0"
        >
          <b-row>
            <b-col md="12">
              <b-col
                md="12"
                style="font-size: 1.5em;"
                class="mb-2"
              >
                {{ $t('Balance') }}
              </b-col>
              <b-col
                md="12"
                style="font-size: 2.5em;"
              >
                <p :class="{'negative-balance': kycWalletBalance.balance < 0}">
                  {{ toBalance(kycWalletBalance.balance) }}
                </p>
              </b-col>
            </b-col>
          </b-row>
          <div class="container">
            <div class="refresh-button">
              <Button
                v-if="getUserPermission('VIEW_KYC_WALLET')"
                :label="$t('Refresh')"
                size="md"
                class="mt-3"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="refetchAPI"
              />
            </div>
            <div class="topup-button">
              <Button
                :label="$t('Restock KYC Wallet')"
                size="md"
                class="mt-3"
                @click="initializePay"
              />
            </div>
          </div>
        </b-col>
      </b-row>
      <b-modal
        id="initialize-payment-modal"
        ref="initialize-payment-modal"
        :ok-title="$t('Top-Up')"
        :cancel-title="$t('Cancel')"
        cancel-variant="outline-secondary"
        :ok-disabled="isLoading"
        centered
        size="sm"
        no-close-on-backdrop
        :title="$t('Restock KYC Wallet')"
        @ok="handleSubmit"
        @hidden="resetModal"
      >
        <validation-observer ref="topupRules">
          <b-form @submit.prevent="handleSubmit">
            <b-row class="match-height">

              <b-col md="12">
                <InputNumberField
                  id="amount"
                  v-model="topupData.amount"
                  :label="$t('Amount')"
                  :placeholder="$t('Amount')"
                  type="number"
                  :error-name="$t('Amount')"
                  rules="required"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
        <template
          v-if="isReadonly"
          #modal-footer="{ }"
        >
          <div />
        </template>
      </b-modal>
    </div>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import PaystackPop from '@paystack/inline-js'
import { permissionMixin } from '@core/mixins/permission'
import InputNumberField from '../../../forms/InputNumberField.vue'
import { toBalance, toKobo } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'

import Button from '../../../components/button/Button.vue'
import { operationPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BForm,
    Button,
    ValidationObserver,
    FullScreenSpinner,
    InputNumberField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toBalance,
      isBusy: true,
      showSpinner: false,
      isLoading: false,
      isDisabled: false,
      sBusy: false,
      required,
      positive,
      endOfYearClosureList: [],
      kycWalletBalance: '',
      topupData: {
        amount: 0,
      },
    }
  },
  async mounted() {
    this.fetchKYCWalletBalance()
  },
  methods: {
    ...mapActions({
      getKYCWalletActions: 'operations/getKYCWalletActions',
      initializePaymentWithAmount: 'admin/initializePaymentWithAmount',
    }),
    async fetchKYCWalletBalance() {
      try {
        this.showSpinner = true
        const response = await this.getKYCWalletActions({
          permission: operationPermissions.VIEW_KYC_WALLET,
        })
        const { data, status } = response
        if (status === 200 && data) {
          this.kycWalletBalance = data
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    initializePay() {
      this.$root.$emit('bv::show::modal', 'initialize-payment-modal')
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.topupRules.validate().then(async success => {
        if (success) {
          const topUpText = this.$t('Are you sure you want to Top-Up your wallet with')
          this.confirm(this.payWithPaystack, { text: `${topUpText} ${toBalance(this.topupData.amount * 100)}?` })
        }
      })
    },
    async payWithPaystack() {
      try {
        this.hideModal('initialize-payment-modal')
        const formData = {
          amount: toKobo(this.topupData.amount),
          subscriptionType: 4,
        }
        const response = await this.initializePaymentWithAmount({
          formData,
          permission: operationPermissions.VIEW_KYC_WALLET,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          const accessCode = data.data.access_code
          const popup = new PaystackPop()
          popup.resumeTransaction(accessCode)
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    refetchAPI() {
      this.isLoading = true
      this.isDisabled = true
      this.fetchKYCWalletBalance()
      this.isLoading = false
      this.isDisabled = false
    },
    resetModal() {
      this.topupData = {}
    },
  },
}
</script>

<style scoped>
.negative-balance {
        color: red;
}
.container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}
.refresh-button {
  text-align: left;
  flex: 1;
}
.topup-button {
  text-align: right;
  flex: 1;
}
</style>
